/**
 * useSessionSplitting - Extracted splitting/assignment logic from SessionDetailsModal.
 *
 * This hook encapsulates all state and handlers related to:
 *  - Splitting a production session across multiple stations (station split entries)
 *  - Saving assignment changes (station, operator, packer, weight mode) for existing sub-sessions
 *  - Unified save (update existing + create new + process pending materials + subdivisions)
 *  - Station change confirmation (for active/paused sub-sessions requiring a reason)
 *  - Station conflict / priority queue confirmation
 *  - Subdivide modal (splitting weight from one sub-session into a new one)
 *  - Add material (staging new materials from AddMaterialModal)
 *  - Unsaved changes detection and discard confirmation
 *  - Auto-start splitting for multi-material sessions
 *  - Re-populating form entries when sub-sessions load
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQueryClient, UseMutationResult } from 'react-query';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../../../../i18n/LanguageContext';
import productionService, { StationAllocation, SubSession } from '../../../../services/productionService';
import stationManagementService, { StationWithDetails } from '../../../../services/stationManagementService';
import { AddMaterialData } from '../AddMaterialModal';
import { DEFAULT_YIELD } from '../../../../hooks/useYieldFactor';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Material interface for multi-material sessions (mirrors SessionDetailsModal) */
interface SessionMaterial {
  sub_session_number?: string;
  item_number: string;
  transfer_number?: string;
  item_name?: string;
  item_english_name?: string;
  item_chinese_name?: string;
  allocated_weight?: number;
  remainder_weight?: number;
  cases?: number;
  yield_factor?: number;
  yield_factor_source?: string;
  brand?: string;
  material_location_code?: string;
  date_code?: string;
}

/** Production session shape used by this hook */
interface ProductionSession {
  session_number: string;
  product_sku: string;
  product_name: string;
  product_chinese_name?: string;
  status: string;
  station_id?: number;
  station_name?: string;
  raw_material_item_number?: string;
  raw_material_name?: string;
  raw_material_chinese_name?: string;
  raw_material_yield_factor?: number;
  raw_material_lot?: string;
  initial_material_weight?: number;
  target_output_weight?: number;
  actual_output_weight?: number;
  waste_weight?: number;
  yield_percentage?: number;
  started_at?: string | null;
  completed_at?: string | null;
  scheduled_date?: string;
  date_code?: string;
  total_boxes?: number;
  expected_boxes?: number;
  packing?: string;
  unit_weight?: number;
  brand?: string;
  material_location_code?: string;
  source_location?: string;
  linked_transfer_number?: string;
  notes?: string;
  assigned_operator_id?: string | number;
  assigned_operator_name?: string;
  created_by_name?: string;
  created_at?: string;
  materials?: SessionMaterial[];
}

/** Station allocation entry for splitting */
export interface StationSplitEntry {
  sub_session_number?: string;
  station_id: number | null;
  station_name: string;
  material_weight: string;
  operator_id: string | null;
  packer_id: string | null;
  weight_mode: 'fixed' | 'catch';  // Fixed = auto-use product unit_weight, Catch = manual entry
  // For multi-material auto-split
  material_item_number?: string;
  material_transfer_number?: string;
  material_name?: string;
  // For pending subdivisions (staged locally until Save is clicked)
  subdivided_from?: string;         // Original sub_session_number this was split from
  subdivide_reason?: string;        // Reason for split
  subdivide_reason_other?: string;  // Custom reason if 'other' selected
}

/** Calculate minimum raw material weight needed to produce 1 box */
export const calculateMinWeightForOneBox = (unitWeight: number, yieldFactor: number): number => {
  if (!unitWeight || unitWeight <= 0 || !yieldFactor || yieldFactor <= 0) {
    return 0; // Can't calculate
  }
  return unitWeight / yieldFactor; // yieldFactor is decimal (e.g. 0.922)
};

// ─── Hook params ─────────────────────────────────────────────────────────────

export interface UseSessionSplittingParams {
  session: ProductionSession;
  sessionData: ProductionSession | undefined;
  subSessions: SubSession[];
  stations: StationWithDetails[];
  canEdit: boolean;
  canEditSubSession: (ss: SubSession) => boolean;
  isReadOnly: boolean;
  onClose: () => void;
  splitSessionMutation: UseMutationResult<any, any, StationAllocation[], any>;
  user: any; // from useAuth
}

interface PendingSessionMaterialPayload {
  item_number: string;
  transfer_number: string | null;
  allocated_weight: number;
  cases: number;
  yield_factor: number;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSessionSplitting({
  session,
  sessionData,
  subSessions,
  stations,
  canEdit,
  canEditSubSession,
  isReadOnly,
  onClose,
  splitSessionMutation,
  user,
}: UseSessionSplittingParams) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // ── Split session state ──────────────────────────────────────────────────

  const [isSplitting, setIsSplitting] = useState(false);
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const [stationSplitEntries, setStationSplitEntries] = useState<StationSplitEntry[]>([
    { station_id: null, station_name: '', material_weight: '', operator_id: null, packer_id: null, weight_mode: 'fixed' }
  ]);

  // ── Add material modal state ─────────────────────────────────────────────

  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [pendingMaterials, setPendingMaterials] = useState<AddMaterialData[]>([]);

  // ── Subdivide modal state ────────────────────────────────────────────────

  const [showSubdivideModal, setShowSubdivideModal] = useState(false);
  const [subdivideSubSessionId, setSubdivideSubSessionId] = useState<string | null>(null);
  const [subdivideWeight, setSubdivideWeight] = useState('');
  const [subdivideReason, setSubdivideReason] = useState('');
  const [subdivideReasonOther, setSubdivideReasonOther] = useState('');
  const [subdivideStationId, setSubdivideStationId] = useState<number | null>(null);
  const [isSubdividing, setIsSubdividing] = useState(false);

  // ── Unsaved changes confirmation state ───────────────────────────────────

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  // ── Station change confirmation state (for active/paused sub-sessions) ──

  const [showStationChangeModal, setShowStationChangeModal] = useState(false);
  const [stationChangeReason, setStationChangeReason] = useState('');
  const [stationChangeOtherReason, setStationChangeOtherReason] = useState('');
  const [pendingStationChange, setPendingStationChange] = useState<{
    subSessionNumber: string;
    newStationId: number;
    entryIndex: number;
  } | null>(null);

  // ── Station conflict (priority queue) confirmation state ─────────────────

  const [stationConflictInfo, setStationConflictInfo] = useState<{
    targetStation: { station_id: number; station_name: string };
    activeSubSession: { sub_session_number: string; status: string };
    queueCount: number;
    subSessionToMove: string;
    pendingStationId: number;
    entryIndex: number;
  } | null>(null);

  const buildPendingSessionMaterialsPayload = useCallback((): {
    raw_materials: Array<{ item_number: string; transfer_number: string | null; allocated_weight: number; cases: number }>;
    target_output_weight: number;
    total_boxes: number;
  } => {
    const materialMap = new Map<string, PendingSessionMaterialPayload>();
    const currentMaterials = (sessionData?.materials || session.materials || []).filter((material): material is SessionMaterial =>
      !!material?.item_number
    );

    for (const material of currentMaterials) {
      const itemNumber = material.item_number;
      const transferNumber = material.transfer_number || null;
      const key = `${itemNumber}|${transferNumber || ''}`;
      const existing = materialMap.get(key);
      const allocatedWeight = Number(material.allocated_weight) || 0;
      const cases = Number(material.cases) || 0;
      const yieldFactor = Number(material.yield_factor) || DEFAULT_YIELD;

      if (existing) {
        existing.allocated_weight += allocatedWeight;
        existing.cases += cases;
        existing.yield_factor = existing.yield_factor || yieldFactor;
      } else {
        materialMap.set(key, {
          item_number: itemNumber,
          transfer_number: transferNumber,
          allocated_weight: allocatedWeight,
          cases,
          yield_factor: yieldFactor,
        });
      }
    }

    for (const pendingMaterial of pendingMaterials) {
      const key = `${pendingMaterial.item_number}|${pendingMaterial.transfer_number || ''}`;
      const existing = materialMap.get(key);
      const cases = pendingMaterial.estimatedBoxes || 0;
      const yieldFactor = Number(pendingMaterial.yield_factor) || DEFAULT_YIELD;

      if (existing) {
        existing.allocated_weight += pendingMaterial.weight;
        existing.cases += cases;
        existing.yield_factor = existing.yield_factor || yieldFactor;
      } else {
        materialMap.set(key, {
          item_number: pendingMaterial.item_number,
          transfer_number: pendingMaterial.transfer_number || null,
          allocated_weight: pendingMaterial.weight,
          cases,
          yield_factor: yieldFactor,
        });
      }
    }

    const aggregatedMaterials = Array.from(materialMap.values()).filter(material => material.allocated_weight > 0);
    const targetOutputWeight = aggregatedMaterials.reduce((sum, material) => {
      return sum + (material.allocated_weight * (material.yield_factor || DEFAULT_YIELD));
    }, 0);
    const totalBoxes = session.unit_weight && session.unit_weight > 0
      ? Math.floor(targetOutputWeight / session.unit_weight)
      : aggregatedMaterials.reduce((sum, material) => sum + (material.cases || 0), 0);

    return {
      raw_materials: aggregatedMaterials.map(({ item_number, transfer_number, allocated_weight, cases }) => ({
        item_number,
        transfer_number,
        allocated_weight,
        cases,
      })),
      target_output_weight: targetOutputWeight,
      total_boxes: totalBoxes,
    };
  }, [pendingMaterials, session.materials, session.unit_weight, sessionData?.materials]);

  // ── Computed: canSplit ────────────────────────────────────────────────────

  // Check if session can be split (needs materials and correct status)
  // Allow splitting/reassigning for planned, unassigned, pending, ready, and active statuses
  const canSplit = session.materials && session.materials.length > 0 &&
    ['received', 'unassigned', 'pending', 'ready', 'active'].includes(session.status);

  // ── Computed: hasMultipleMaterials ────────────────────────────────────────

  // Check if this is a multi-material session (more than 1 unique material OR more than 1 sub-session)
  const hasMultipleMaterials = (session.materials && session.materials.length > 1) || subSessions.length > 1;

  // ── Computed: totalAllocatedWeight ────────────────────────────────────────

  const totalAllocatedWeight = useMemo(() => {
    return stationSplitEntries.reduce((sum, entry) => {
      return sum + (parseFloat(entry.material_weight) || 0);
    }, 0);
  }, [stationSplitEntries]);

  // ── Computed: isSplitValid ───────────────────────────────────────────────

  // Validates: weights entered, weights match session total, at least 1 entry
  // Note: Duplicate stations are allowed - same station can be assigned to multiple sub-sessions
  const isSplitValid = useMemo(() => {
    const sessionWeight = Number(session.initial_material_weight) || 0;

    // Exclude read-only sub-sessions (cancelled/completed) from validation. They are not
    // editable and carry weight 0.00, so they must not gate saving edits to the remaining
    // sub-sessions (WCO-2375).
    const nonEditableSubNumbers = new Set(
      subSessions.filter(ss => !canEditSubSession(ss)).map(ss => ss.sub_session_number)
    );
    const validatableEntries = stationSplitEntries.filter(
      e => !e.sub_session_number || !nonEditableSubNumbers.has(e.sub_session_number)
    );

    const allWeightsEntered = validatableEntries.every(e => parseFloat(e.material_weight) > 0);

    // In multi-material mode (each entry has its own material), weights are pre-filled and don't need to match session total
    const isMultiMaterialMode = validatableEntries[0]?.material_item_number;
    // Use larger tolerance (0.5 lbs) to account for rounding differences
    const weightsMatch = isMultiMaterialMode || Math.abs(totalAllocatedWeight - sessionWeight) < 0.5;

    // In multi-material mode, we need at least 1 entry per material (entries are pre-created)
    // In manual mode, we need at least 1 entry (allow single station assignment or re-splitting)
    const minEntriesValid = validatableEntries.length >= 1;

    return allWeightsEntered && weightsMatch && minEntriesValid;
  }, [stationSplitEntries, totalAllocatedWeight, session.initial_material_weight, subSessions, canEditSubSession]);

  // ── Computed: entriesBelowMinWeight ──────────────────────────────────────

  // Find entries with weight below minimum for 1 box (informational warning only)
  const entriesBelowMinWeight = useMemo(() => {
    const unitWeight = session.unit_weight || 0;
    if (!unitWeight || unitWeight <= 0) return [];

    // Get yield factor: for multi-material use entry's material, for single-material use first material
    const isMultiMaterial = session.materials && session.materials.length > 1;
    const defaultYield = session.materials?.[0]?.yield_factor
      || session.raw_material_yield_factor
      || 1.0; // Default 100% (decimal)

    return stationSplitEntries.filter((entry, index) => {
      const entryWeight = parseFloat(entry.material_weight) || 0;
      if (entryWeight <= 0) return false;

      // For multi-material, use entry's material yield; for single-material, use default
      let yieldFactor = defaultYield;
      if (isMultiMaterial && session.materials?.[index]) {
        yieldFactor = session.materials[index].yield_factor
          || defaultYield;
      }

      const minWeight = calculateMinWeightForOneBox(unitWeight, yieldFactor);
      return minWeight > 0 && entryWeight < minWeight;
    });
  }, [stationSplitEntries, session.unit_weight, session.materials, session.raw_material_yield_factor]);

  // ── Computed: hasChanges ─────────────────────────────────────────────────

  // Detect if any changes were made compared to original sub-sessions
  // Returns true if: new entries exist, pending materials exist, or any existing entry has changed
  const hasChanges = useMemo(() => {
    // Helper to normalize empty/null/undefined to null for comparison
    const normalize = (val: string | number | null | undefined): string | null => {
      if (val === undefined || val === null || val === '') return null;
      return String(val);
    };

    // Pending materials from Add Material modal = changes
    if (pendingMaterials.length > 0) return true;

    // New entries (without sub_session_number) = changes
    const newEntries = stationSplitEntries.filter(e => !e.sub_session_number);
    if (newEntries.length > 0) return true;

    // Check if any existing entry has changes compared to original sub-session
    for (const entry of stationSplitEntries) {
      if (!entry.sub_session_number) continue;
      const original = subSessions.find(ss => ss.sub_session_number === entry.sub_session_number);
      if (!original) continue;

      // Compare station (both can be null or number)
      if (entry.station_id !== original.station_id) return true;
      // Compare operator (normalize to handle undefined/null/empty string)
      // Use same fields as initialization: operator_employee_number || assigned_operator
      if (normalize(entry.operator_id) !== normalize(original.operator_employee_number || original.assigned_operator)) return true;
      // Compare packer (normalize to handle undefined/null/empty string)
      // Use same fields as initialization: packer_employee_number || assigned_packer
      if (normalize(entry.packer_id) !== normalize(original.packer_employee_number || original.assigned_packer)) return true;
      // Compare weight mode (default to 'fixed' if not set)
      if ((entry.weight_mode || 'fixed') !== (original.weight_mode || 'fixed')) return true;
      // Compare weight (with tolerance for floating point and rounding)
      const entryWeight = parseFloat(entry.material_weight) || 0;
      const originalWeight = Number(original.initial_material_weight) || 0;
      if (Math.abs(entryWeight - originalWeight) > 0.05) return true;
    }

    return false;
  }, [stationSplitEntries, subSessions, pendingMaterials]);

  // ── Handler: handleStartSplitting ────────────────────────────────────────

  const handleStartSplitting = () => {
    // Check if this is a multi-material session (each material must go to one station - locked mode)
    const isMultiMaterialSession = session.materials && session.materials.length > 1;

    // FIRST: If sub-sessions exist (1 or more), use their data (preserves existing station/operator assignments)
    // This is checked BEFORE session.materials to ensure existing assignments are not overwritten
    if (subSessions.length >= 1) {
      // Match sub-sessions with materials by sub_session_number (not index — materials can have multiple rows per item)
      const entries: StationSplitEntry[] = subSessions.map((ss) => {
        // Find the first material that belongs to this sub-session
        const matchedMaterial = isMultiMaterialSession
          ? (session.materials?.find((m: any) => m.sub_session_number === ss.sub_session_number) || session.materials?.[0])
          : (session.materials?.[0] || undefined);
        // Fallback to session.raw_material_item_number for single-material sessions without materials array
        const itemNumber = matchedMaterial?.item_number || session.raw_material_item_number || undefined;
        return {
          sub_session_number: ss.sub_session_number,
          station_id: ss.station_id,
          station_name: ss.station_name || '',
          material_weight: String(Number(ss.initial_material_weight || 0).toFixed(2)),
          operator_id: ss.operator_employee_number?.toString() || ss.assigned_operator?.toString() || null,
          packer_id: ss.packer_employee_number?.toString() || ss.assigned_packer?.toString() || null,
          weight_mode: ss.weight_mode || 'fixed',
          material_item_number: itemNumber,
          material_transfer_number: matchedMaterial?.transfer_number || undefined,
          material_name: matchedMaterial?.item_english_name || itemNumber || undefined
        };
      });
      setStationSplitEntries(entries);
      setIsSplitting(true);
      return;
    }

    // SECOND: If multiple materials but no sub-sessions yet, create entries from materials
    if (session.materials && session.materials.length > 1) {
      const entries: StationSplitEntry[] = session.materials.map(m => ({
        station_id: null,
        station_name: '',
        material_weight: String(Number(m.allocated_weight || 0).toFixed(2)),
        operator_id: null,
        packer_id: null,
        weight_mode: 'fixed' as const,
        material_item_number: m.item_number,
        material_transfer_number: m.transfer_number,
        material_name: m.item_english_name || m.item_number
      }));
      setStationSplitEntries(entries);
      setIsSplitting(true);
      return;
    }

    // Single material - populate material_item_number from session.materials[0]
    // so AddMaterialModal can find matching sub-sessions by item_number
    const singleMaterial = session.materials?.[0] || undefined;
    const singleItemNumber = singleMaterial?.item_number || session.raw_material_item_number || undefined;
    const singleTransferNumber = singleMaterial?.transfer_number || undefined;
    const singleMaterialName = singleMaterial?.item_english_name || singleItemNumber || undefined;

    // Check for existing station/operator from sub-session (if single sub-session exists)
    const existingStationId = subSessions.length === 1 ? subSessions[0].station_id : null;
    const existingOperatorId = subSessions.length === 1
      ? (subSessions[0].assigned_operator?.toString() || null)
      : null;
    const existingWeightMode = subSessions.length === 1 ? (subSessions[0].weight_mode || 'fixed') : 'fixed';

    if (existingStationId) {
      const station = stations.find(s => s.station_id === existingStationId);
      setStationSplitEntries([
        {
          station_id: existingStationId,
          station_name: station?.station_name || '',
          material_weight: '',
          operator_id: existingOperatorId,
          packer_id: subSessions.length === 1 ? (subSessions[0].assigned_packer?.toString() || null) : null,
          weight_mode: existingWeightMode,
          material_item_number: singleItemNumber,
          material_transfer_number: singleTransferNumber,
          material_name: singleMaterialName
        },
        {
          station_id: null,
          station_name: '',
          material_weight: '',
          operator_id: null,
          packer_id: null,
          weight_mode: 'fixed',
          material_item_number: singleItemNumber,
          material_transfer_number: singleTransferNumber,
          material_name: singleMaterialName
        }
      ]);
    } else {
      setStationSplitEntries([
        {
          station_id: null,
          station_name: '',
          material_weight: '',
          operator_id: null,
          packer_id: null,
          weight_mode: 'fixed',
          material_item_number: singleItemNumber,
          material_transfer_number: singleTransferNumber,
          material_name: singleMaterialName
        }
      ]);
    }
    setIsSplitting(true);
  };

  // ── Handler: removeStationEntry ──────────────────────────────────────────

  const removeStationEntry = (index: number) => {
    if (stationSplitEntries.length <= 1) return;

    const entryToRemove = stationSplitEntries[index];

    // If this entry was a pending subdivision, restore weight to the original entry
    if (entryToRemove.subdivided_from) {
      const removedWeight = parseFloat(entryToRemove.material_weight) || 0;
      setStationSplitEntries(prev => {
        // Find the original entry and restore its weight
        const updated = prev.map(entry => {
          if (entry.sub_session_number === entryToRemove.subdivided_from) {
            const currentWeight = parseFloat(entry.material_weight) || 0;
            return {
              ...entry,
              material_weight: (currentWeight + removedWeight).toString()
            };
          }
          return entry;
        });
        // Remove the entry
        return updated.filter((_, i) => i !== index);
      });
    } else {
      // Regular removal
      setStationSplitEntries(prev => prev.filter((_, i) => i !== index));
    }
  };

  // ── Handler: updateStationEntry ──────────────────────────────────────────

  const updateStationEntry = (index: number, field: string, value: string | number | null) => {
    setStationSplitEntries(prev => prev.map((entry, i) => {
      if (i !== index) return entry;
      if (field === 'station_id') {
        const station = stations.find(s => s.station_id === value);
        return {
          ...entry,
          station_id: value as number | null,
          station_name: station?.station_name || ''
        };
      }
      return { ...entry, [field]: value };
    }));
  };

  // ── Handler: handleSplitSession ──────────────────────────────────────────

  const handleSplitSession = async () => {
    if (!isSplitValid) return;

    // Check if this is an update to existing active sub-sessions (not a new split)
    const isUpdatingExisting = ['active', 'paused'].includes(session.status) && subSessions.length > 0;

    if (isUpdatingExisting) {
      // For active sessions, just update the existing sub-sessions in-place
      try {
        for (const entry of stationSplitEntries) {
          // Find the existing sub-session for this station
          const existingSubSession = subSessions.find(ss => ss.station_id === entry.station_id);
          if (existingSubSession) {
            // Update weight mode
            try {
              await stationManagementService.updateSubSessionWeightMode(existingSubSession.sub_session_number, entry.weight_mode);
            } catch (err: any) {
              console.error(`Failed to update weight mode for sub-session at station ${entry.station_id}:`, err);
            }

            // Update operator if changed
            const currentOperatorId = existingSubSession.assigned_operator?.toString() || null;
            if (entry.operator_id !== currentOperatorId) {
              try {
                if (entry.operator_id) {
                  await stationManagementService.assignSubSessionOperator(existingSubSession.sub_session_number, entry.operator_id);
                }
              } catch (err: any) {
                const errorMsg = err?.response?.data?.error || err?.message || 'Failed to assign operator';
                console.error(`Failed to assign operator to sub-session at station ${entry.station_id}:`, err);
                toast.error(`${entry.station_name}: ${errorMsg}`);
              }
            }

            // Update packer if changed
            const currentPackerId = existingSubSession.assigned_packer?.toString() || null;
            if (entry.packer_id !== currentPackerId) {
              try {
                await stationManagementService.assignSubSessionPacker(existingSubSession.sub_session_number, entry.packer_id, user?.user_id);
              } catch (err: any) {
                const errorMsg = err?.response?.data?.error || err?.message || 'Failed to assign packer';
                console.error(`Failed to assign packer to sub-session at station ${entry.station_id}:`, err);
                toast.error(`${entry.station_name}: ${errorMsg}`);
              }
            }
          }
        }

        toast.success(t('sessionDetails.sessionUpdated') || 'Session updated successfully');
        setIsSplitting(false);

        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['sub-sessions', session.session_number]);
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['supervisor-stations']);
      } catch (error) {
        console.error('Error updating session:', error);
        toast.error('Failed to update session');
      }
      return;
    }

    // Validate required fields for new split
    if (!session.materials || session.materials.length === 0) {
      toast.error('Cannot split: session is missing material information');
      return;
    }

    let stationAllocations: StationAllocation[];

    // Check if this is multi-material mode (each entry has its own material)
    if (hasMultipleMaterials && stationSplitEntries[0]?.material_item_number) {
      // Multi-material mode: each entry is a separate material going to its own station
      stationAllocations = stationSplitEntries.map(entry => ({
        station_id: entry.station_id!,
        materials: [{
          item_number: entry.material_item_number || '',
          transfer_number: entry.material_transfer_number || '',
          allocated_weight: parseFloat(entry.material_weight) || 0
        }],
        weight_mode: entry.weight_mode
      }));
    } else {
      // Single material or manual split mode: distribute weight proportionally
      const sessionMaterials = session.materials && session.materials.length > 0
        ? session.materials
        : [{ item_number: session.raw_material_item_number || '', transfer_number: session.raw_material_lot || '' }];

      const totalMaterialWeight = sessionMaterials.reduce((sum, m) => sum + (Number(m.allocated_weight) || 0), 0);

      stationAllocations = stationSplitEntries.map(entry => {
        const stationWeight = parseFloat(entry.material_weight);
        const weightRatio = totalMaterialWeight > 0 ? stationWeight / totalMaterialWeight : 1 / stationSplitEntries.length;

        return {
          station_id: entry.station_id!,
          materials: sessionMaterials.map(m => ({
            item_number: m.item_number,
            transfer_number: m.transfer_number || '',
            allocated_weight: Number(m.allocated_weight || 0) * weightRatio
          })),
          weight_mode: entry.weight_mode
        };
      });
    }

    try {
      // First, create the sub-sessions
      await splitSessionMutation.mutateAsync(stationAllocations);

      // After split is created, fetch the new sub-sessions and assign operators
      const newSubSessions = await productionService.getSubSessions(session.session_number);

      // Assign operators and update weight_mode for sub-sessions
      for (const entry of stationSplitEntries) {
        // Find the sub-session for this station
        const subSession = newSubSessions.find(ss => ss.station_id === entry.station_id);
        if (subSession) {
          // Assign operator if selected
          if (entry.operator_id) {
            try {
              await stationManagementService.assignSubSessionOperator(subSession.sub_session_number, entry.operator_id);
            } catch (err: any) {
              const errorMsg = err?.response?.data?.error || err?.message || 'Failed to assign operator';
              console.error(`Failed to assign operator to sub-session at station ${entry.station_id}:`, err);
              toast.error(`${entry.station_name}: ${errorMsg}`);
              // Continue with other assignments even if one fails
            }
          }
          // Assign packer if selected
          if (entry.packer_id) {
            try {
              await stationManagementService.assignSubSessionPacker(subSession.sub_session_number, entry.packer_id, user?.user_id);
            } catch (err: any) {
              const errorMsg = err?.response?.data?.error || err?.message || 'Failed to assign packer';
              console.error(`Failed to assign packer to sub-session at station ${entry.station_id}:`, err);
              toast.error(`${entry.station_name}: ${errorMsg}`);
            }
          }
          // Update weight mode if not default
          if (entry.weight_mode === 'catch') {
            try {
              await stationManagementService.updateSubSessionWeightMode(subSession.sub_session_number, entry.weight_mode);
            } catch (err: any) {
              console.error(`Failed to update weight mode for sub-session at station ${entry.station_id}:`, err);
              // Continue even if weight mode update fails
            }
          }
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['sub-sessions', session.session_number]);
      queryClient.invalidateQueries(['supervisor-stations']);
    } catch (error) {
      // Errors are handled by individual mutation onError callbacks
    }
  };

  // ── Handler: handleSaveSubSessionAssignments ─────────────────────────────

  const handleSaveSubSessionAssignments = async () => {
    if (subSessions.length === 0) return;

    setIsSavingAssignments(true);
    try {
      // Match entries to sub-sessions by index when stations don't match
      // This handles the case where sub-sessions exist but don't have stations assigned yet
      for (let i = 0; i < stationSplitEntries.length; i++) {
        const entry = stationSplitEntries[i];

        // Match by sub_session_number (stable), fall back to index for new entries
        let existingSubSession = entry.sub_session_number
          ? subSessions.find(ss => ss.sub_session_number === entry.sub_session_number)
          : subSessions[i];

        if (existingSubSession) {
          // Skip non-editable sub-sessions (active, paused, completed, cancelled)
          if (!canEditSubSession(existingSubSession)) continue;

          // Assign or unassign station if it changed
          if (existingSubSession.station_id !== entry.station_id) {
            try {
              await stationManagementService.assignSubSessionStation(existingSubSession.sub_session_number, entry.station_id);
            } catch (err: any) {
              console.error(`Failed to ${entry.station_id ? 'assign' : 'unassign'} station:`, err);
              toast.error(`Failed to ${entry.station_id ? 'assign' : 'unassign'} station: ${err?.response?.data?.error || err?.message}`);
            }
          }

          // Update weight mode
          try {
            await stationManagementService.updateSubSessionWeightMode(existingSubSession.sub_session_number, entry.weight_mode);
          } catch (err: any) {
            console.error(`Failed to update weight mode for sub-session at station ${entry.station_id}:`, err);
          }

          // Update operator if changed
          const currentOperatorId = existingSubSession.assigned_operator?.toString() || null;
          if (entry.operator_id !== currentOperatorId) {
            try {
              if (entry.operator_id) {
                await stationManagementService.assignSubSessionOperator(existingSubSession.sub_session_number, entry.operator_id);
              }
            } catch (err: any) {
              const errorMsg = err?.response?.data?.error || err?.message || 'Failed to assign operator';
              console.error(`Failed to assign operator to sub-session at station ${entry.station_id}:`, err);
              toast.error(`${entry.station_name}: ${errorMsg}`);
            }
          }

          // Update packer if changed
          const currentPackerId = existingSubSession.assigned_packer?.toString() || null;
          if (entry.packer_id !== currentPackerId) {
            try {
              await stationManagementService.assignSubSessionPacker(
                existingSubSession.sub_session_number,
                entry.packer_id,
                user?.user_id
              );
            } catch (err: any) {
              const errorMsg = err?.response?.data?.error || err?.message || t('sessionDetails.failedToAssignPacker');
              console.error(`Failed to assign packer to sub-session at station ${entry.station_id}:`, err);
              toast.error(`${entry.station_name}: ${errorMsg}`);
            }
          }
        }
      }

      toast.success(t('sessionDetails.sessionUpdated') || 'Session updated successfully');

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['sub-sessions', session.session_number]);
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['supervisor-stations']);
    } catch (error) {
      console.error('Error saving assignments:', error);
      toast.error('Failed to save assignments');
    } finally {
      setIsSavingAssignments(false);
    }
  };

  // ── Handler: handleUnifiedSave ───────────────────────────────────────────

  // Unified save handler - handles both updating existing and creating new sub-sessions
  const handleUnifiedSave = async () => {
    if (!isSplitValid) return;

    // Separate entries into: existing (update), pending subdivisions, and regular new (create)
    const existingEntries = stationSplitEntries.filter(e => e.sub_session_number);
    const pendingSubdivisions = stationSplitEntries.filter(e => !e.sub_session_number && e.subdivided_from);
    // Exclude entries with material_item_number - those are from Add Material modal and handled in step 3 (pendingMaterials)
    const regularNewEntries = stationSplitEntries.filter(e => !e.sub_session_number && !e.subdivided_from && !e.material_item_number);

    const supervisorDisplayName = user?.fullName || user?.full_name || user?.username || 'supervisor';
    setIsSavingAssignments(true);
    try {
      // 0. Process pending subdivisions first (uses backend subdivide endpoint)
      for (const entry of pendingSubdivisions) {
        if (!entry.subdivided_from || !entry.subdivide_reason) continue;
        const splitWeight = parseFloat(entry.material_weight) || 0;
        if (splitWeight <= 0) continue;

        try {
          await stationManagementService.subdivideSubSession(
            entry.subdivided_from,
            splitWeight,
            {
              reason: entry.subdivide_reason,
              reason_other: entry.subdivide_reason_other,
              target_station_id: entry.station_id || undefined
            }
          );
        } catch (err: any) {
          console.error('Failed to subdivide:', err);
          const errorMsg = err?.response?.data?.error || err?.message || 'Failed to subdivide sub-session';
          toast.error(errorMsg);
        }
      }

      // 1. Update existing sub-sessions
      for (const entry of existingEntries) {
        const subSession = subSessions.find(ss => ss.sub_session_number === entry.sub_session_number);
        if (!subSession || !canEditSubSession(subSession)) continue;

        // Update weight for planned/ready/active/paused (not completed/cancelled)
        // Skip if pending material will update this sub-session's weight via addMaterialToSession
        const hasPendingMaterial = pendingMaterials.some(m =>
          m.willDistribute && m.targetSubSessionIds?.includes(subSession.sub_session_number)
        );
        if (!hasPendingMaterial && ['received', 'ready', 'active', 'paused'].includes(subSession.status)) {
          const originalWeight = Number(subSession.initial_material_weight) || 0;
          const newWeight = parseFloat(entry.material_weight) || 0;
          if (Math.abs(newWeight - originalWeight) > 0.01) {
            try {
              await stationManagementService.updateSubSessionWeight(subSession.sub_session_number, newWeight);
            } catch (err: any) {
              console.error(`Failed to update weight:`, err);
              toast.error(`Failed to update weight: ${err?.response?.data?.error || err?.message}`);
            }
          }
        }

        // Update station if changed (including unassign when entry.station_id is null)
        if (entry.station_id !== subSession.station_id) {
          try {
            await stationManagementService.assignSubSessionStation(subSession.sub_session_number, entry.station_id);
          } catch (err: any) {
            console.error(`Failed to ${entry.station_id ? 'assign' : 'unassign'} station:`, err);
            toast.error(`Failed to ${entry.station_id ? 'assign' : 'unassign'} station: ${err?.response?.data?.error || err?.message}`);
          }
        }

        // Update weight mode only when backend state rules allow it.
        if (entry.weight_mode !== subSession.weight_mode) {
          if (['received', 'ready', 'active'].includes(subSession.status)) {
            try {
              await stationManagementService.updateSubSessionWeightMode(subSession.sub_session_number, entry.weight_mode);
            } catch (err: any) {
              console.error(`Failed to update weight mode:`, err);
              toast.error(err?.response?.data?.error || err?.message || 'Failed to update weight mode');
            }
          } else {
            toast.error(
              `Cannot change weight mode for sub-session ${subSession.sub_session_number} while it is ${subSession.status}.`
            );
          }
        }

        // Update operator if changed
        const currentOperatorId = subSession.assigned_operator?.toString() || null;
        if (entry.operator_id !== currentOperatorId) {
          await stationManagementService.assignSubSessionOperator(subSession.sub_session_number, entry.operator_id || '', supervisorDisplayName);
        }

        // Update packer if changed
        const currentPackerId = subSession.assigned_packer?.toString() || null;
        if (entry.packer_id !== currentPackerId) {
          await stationManagementService.assignSubSessionPacker(subSession.sub_session_number, entry.packer_id, user?.user_id, supervisorDisplayName);
        }
      }

      // 2. Create regular new sub-sessions using addSubSessions (append mode - doesn't delete existing)
      // Note: Pending subdivisions are handled above, so we only process regularNewEntries here
      if (regularNewEntries.length > 0 && session.materials && session.materials.length > 0) {
        const sessionMaterials = session.materials && session.materials.length > 0
          ? session.materials
          : [{ item_number: session.raw_material_item_number || '', transfer_number: session.raw_material_lot || '' }];

        // Build allocations for regular new entries (not subdivisions)
        const allocations: StationAllocation[] = regularNewEntries.map(entry => {
          const entryWeight = parseFloat(entry.material_weight) || 0;
          return {
            station_id: entry.station_id || null,
            materials: sessionMaterials.map(m => ({
              item_number: m.item_number,
              transfer_number: m.transfer_number || '',
              allocated_weight: entryWeight  // Direct weight assignment
            })),
            weight_mode: entry.weight_mode
          };
        });

        // Use addSubSessions endpoint (doesn't delete existing sub-sessions)
        const result = await productionService.addSubSessions(session.session_number, allocations);

        // Assign operators/packers to newly created sub-sessions
        if (result.sub_sessions) {
          for (let i = 0; i < regularNewEntries.length && i < result.sub_sessions.length; i++) {
            const entry = regularNewEntries[i];
            const newSubSession = result.sub_sessions[i];

            if (entry.operator_id) {
              try {
                await stationManagementService.assignSubSessionOperator(newSubSession.sub_session_number, entry.operator_id, supervisorDisplayName);
              } catch (err: any) {
                console.error(`Failed to assign operator to new sub-session:`, err);
              }
            }
            if (entry.packer_id) {
              try {
                await stationManagementService.assignSubSessionPacker(newSubSession.sub_session_number, entry.packer_id, user?.user_id, supervisorDisplayName);
              } catch (err: any) {
                console.error(`Failed to assign packer to new sub-session:`, err);
              }
            }
          }
        }
      }

      // 3. Process pending materials (from Add Material modal)
      // WCO-2428: track adds that fail so failed materials stay staged for retry (instead of being
      // silently dropped) and so the success toast / modal-close below can be suppressed when any
      // add failed. Only populated by the non-pending branch below; stays empty otherwise.
      const failedMaterials: AddMaterialData[] = [];
      if (pendingMaterials.length > 0) {
        if (session.status === 'pending') {
          if (subSessions.length > 1) {
            throw new Error('Adding materials to split pending sessions is not supported by the current save flow yet.');
          }

          const { raw_materials, target_output_weight, total_boxes } = buildPendingSessionMaterialsPayload();
          await productionService.updateProductionSession(session.session_number, {
            product_sku: session.product_sku,
            scheduled_date: session.scheduled_date,
            raw_materials,
            target_output_weight,
            total_boxes,
            source_warehouse: session.source_location,
          });
        } else {
          for (const material of pendingMaterials) {
            try {
              await stationManagementService.addMaterialToSession(
                session.session_number,
                material.item_number,
                material.transfer_number,
                material.weight,
                {
                  reason: material.reason,
                  reason_other: material.reason_other,
                  target_sub_session_numbers: material.targetSubSessionIds,
                  yield_factor: material.yield_factor,
                  // WCO-2607: forward the picked lot so the backend consumes exactly that lot
                  lot_id: material.lot_id
                }
              );
            } catch (err: any) {
              console.error('Failed to add material:', err);
              toast.error(`Failed to add material ${material.item_number}: ${err?.response?.data?.error || err?.message}`);
              failedMaterials.push(material);
            }
          }
        }

        // WCO-2428: keep only the adds that failed staged (so the supervisor can retry); the
        // successfully committed ones are removed. Previously this cleared ALL pending
        // unconditionally, silently losing failed adds.
        setPendingMaterials(failedMaterials);
      }

      // Collect history entries for assignment changes
      const historyEntries: Array<{ action: string; changed_by?: string; notes?: Record<string, unknown>; sub_session_number?: string }> = [];

      for (const entry of existingEntries) {
        const subSession = subSessions.find(ss => ss.sub_session_number === entry.sub_session_number);
        if (!subSession || !canEditSubSession(subSession)) continue;
        const ssNumber = subSession.sub_session_number || undefined;

        // Station changed
        if (entry.station_id !== subSession.station_id) {
          historyEntries.push({
            action: entry.station_id ? 'station_assigned' : 'station_unassigned',
            changed_by: supervisorDisplayName,
            sub_session_number: ssNumber,
            notes: {
              station_name: entry.station_name || null,
              old_station_name: subSession.station_name || null,
              sub_session_number: ssNumber || null,
            }
          });
        }

        // NOTE: Operator and packer assignments are already logged by the backend
        // (station_activity_log via assign-operator/assign-packer endpoints), so we skip them here.

        // Weight mode changed
        if (
          entry.weight_mode !== subSession.weight_mode &&
          ['received', 'ready', 'active'].includes(subSession.status)
        ) {
          historyEntries.push({
            action: 'weight_mode_changed',
            changed_by: supervisorDisplayName,
            sub_session_number: ssNumber,
            notes: {
              old_mode: subSession.weight_mode || 'fixed',
              new_mode: entry.weight_mode,
              station_name: entry.station_name || subSession.station_name || null,
              sub_session_number: ssNumber || null,
            }
          });
        }
      }

      // Send all history entries in one batch call (fire-and-forget, don't block save)
      if (historyEntries.length > 0) {
        productionService.addSessionHistory(session.session_number, historyEntries).catch(err => {
          console.error('Failed to log assignment history:', err);
        });
      }

      // Invalidate queries to refresh data (reflects whatever DID land, incl. partial success)
      queryClient.invalidateQueries(['sub-sessions', session.session_number]);
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['rawMaterials']);
      queryClient.invalidateQueries(['session-history', session.session_number]);

      // WCO-2428: if any pending-material add failed, keep the modal open (those materials remain
      // staged for retry) and skip the success toast — the per-material error toast already fired,
      // and a green "success" here would mask the failure.
      if (failedMaterials.length > 0) {
        return;
      }

      toast.success(t('sessionDetails.sessionUpdated') || 'Session updated successfully');

      // Close modal on successful save
      onClose();
    } catch (error: any) {
      console.error('Error saving session:', error);
      toast.error(error?.response?.data?.error || 'Failed to save session');
    } finally {
      setIsSavingAssignments(false);
    }
  };

  // ── Handler: handleClose (unsaved changes check) ────────────────────────

  const handleClose = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  };

  // ── Handler: handleConfirmDiscard ────────────────────────────────────────

  const handleConfirmDiscard = () => {
    setShowUnsavedChangesModal(false);
    onClose();
  };

  // ── Handler: handleConfirmStationChange ──────────────────────────────────

  // Handle confirmed station change for active/paused sub-sessions (with reason)
  const handleConfirmStationChange = async () => {
    if (!pendingStationChange || !stationChangeReason) return;
    if (stationChangeReason === 'other' && !stationChangeOtherReason.trim()) return;

    setIsSavingAssignments(true);
    try {
      const result = await stationManagementService.assignSubSessionStation(
        pendingStationChange.subSessionNumber,
        pendingStationChange.newStationId,
        {
          reason: stationChangeReason,
          reason_other: stationChangeReason === 'other' ? stationChangeOtherReason.trim() : undefined,
          supervisor_id: user?.id
        }
      );

      // Check if API returned a conflict (station already has active sub-session)
      if (result.status === 'conflict') {
        // Store conflict info and show confirmation modal
        setStationConflictInfo({
          targetStation: result.conflict.targetStation,
          activeSubSession: result.conflict.activeSubSession,
          queueCount: result.conflict.queueCount,
          subSessionToMove: pendingStationChange.subSessionNumber,
          pendingStationId: pendingStationChange.newStationId,
          entryIndex: pendingStationChange.entryIndex
        });
        // Close station change modal, keep pending change info for retry
        setShowStationChangeModal(false);
        return; // Don't proceed until user confirms priority queue
      }

      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['sub-sessions', session.session_number]);
      queryClient.invalidateQueries(['supervisor-stations']);
      toast.success(t('sessionDetails.stationAssigned') || 'Station assigned successfully');
      // Clear all state on success
      setShowStationChangeModal(false);
      setStationChangeReason('');
      setStationChangeOtherReason('');
      setPendingStationChange(null);
    } catch (err: any) {
      console.error('Failed to assign station:', err);
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to assign station';
      toast.error(errorMsg);
      // Revert the station selection on error
      const subSession = subSessions.find(ss => ss.sub_session_number === pendingStationChange.subSessionNumber);
      if (subSession) {
        updateStationEntry(pendingStationChange.entryIndex, 'station_id', subSession.station_id);
      }
      setShowStationChangeModal(false);
      setStationChangeReason('');
      setStationChangeOtherReason('');
      setPendingStationChange(null);
    } finally {
      setIsSavingAssignments(false);
    }
  };

  // ── Handler: handleConfirmQueuePriority ──────────────────────────────────

  // Handle confirmed priority queue (user confirmed they want to queue behind active sub-session)
  const handleConfirmQueuePriority = async () => {
    if (!stationConflictInfo) return;

    setIsSavingAssignments(true);
    try {
      await stationManagementService.assignSubSessionStation(
        stationConflictInfo.subSessionToMove,
        stationConflictInfo.pendingStationId,
        {
          reason: stationChangeReason,
          reason_other: stationChangeReason === 'other' ? stationChangeOtherReason.trim() : undefined,
          supervisor_id: user?.id,
          confirmed: true // Confirm to proceed with priority queue
        }
      );

      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['sub-sessions', session.session_number]);
      queryClient.invalidateQueries(['supervisor-stations']);
      toast.success(t('sessionDetails.queuedWithPriority') || 'Sub-session queued with priority');
    } catch (err: any) {
      console.error('Failed to queue sub-session:', err);
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to queue sub-session';
      toast.error(errorMsg);
      // Revert the station selection on error
      const subSession = subSessions.find(ss => ss.sub_session_number === stationConflictInfo.subSessionToMove);
      if (subSession) {
        updateStationEntry(stationConflictInfo.entryIndex, 'station_id', subSession.station_id);
      }
    } finally {
      setIsSavingAssignments(false);
      setStationConflictInfo(null);
      setStationChangeReason('');
      setStationChangeOtherReason('');
      setPendingStationChange(null);
    }
  };

  // ── Handler: handleCancelQueuePriority ───────────────────────────────────

  // Handle cancel priority queue (user cancelled - revert station selection)
  const handleCancelQueuePriority = () => {
    if (stationConflictInfo) {
      // Revert the station selection
      const subSession = subSessions.find(ss => ss.sub_session_number === stationConflictInfo.subSessionToMove);
      if (subSession) {
        updateStationEntry(stationConflictInfo.entryIndex, 'station_id', subSession.station_id);
      }
    }
    setStationConflictInfo(null);
    setStationChangeReason('');
    setStationChangeOtherReason('');
    setPendingStationChange(null);
  };

  // ── Handler: openSubdivideModal ──────────────────────────────────────────

  // Handle subdivide action - opens the modal for an active/paused sub-session
  const openSubdivideModal = (subSessionNumber: string) => {
    setSubdivideSubSessionId(subSessionNumber);
    setSubdivideWeight('');
    setSubdivideReason('');
    setSubdivideReasonOther('');
    setSubdivideStationId(null);
    setShowSubdivideModal(true);
  };

  // ── Handler: handleAddMaterial ───────────────────────────────────────────

  // Handle adding material from AddMaterialModal - adds to local state, saved with main Save button
  const handleAddMaterial = (data: AddMaterialData) => {
    // Store the pending material data
    setPendingMaterials(prev => [...prev, data]);

    // If it will create a new sub-session (different material), add a new entry to the split entries
    if (!data.willDistribute) {
      // Add new entry for the new material
      const newEntry = {
        station_id: null,
        station_name: '',
        operator_id: null,
        packer_id: null,
        material_weight: data.weight.toString(),
        material_item_number: data.item_number,
        material_transfer_number: data.transfer_number,
        material_item_name: data.item_name,
        yield_percentage: data.yield_factor.toString(),
        weight_mode: 'fixed' as const
      };
      setStationSplitEntries(prev => [...prev, newEntry]);
    } else {
      // If distributing to existing sub-sessions, we need to update their weights proportionally
      // Find entries with matching item_number
      const matchingIndices = stationSplitEntries
        .map((entry, idx) => ({ entry, idx }))
        .filter(({ entry }) => entry.material_item_number === data.item_number);

      if (matchingIndices.length > 0) {
        const totalExisting = matchingIndices.reduce((sum, { entry }) =>
          sum + (parseFloat(entry.material_weight) || 0), 0);

        setStationSplitEntries(prev => prev.map((entry, idx) => {
          const match = matchingIndices.find(m => m.idx === idx);
          if (!match) return entry;

          const currentWeight = parseFloat(entry.material_weight) || 0;
          const proportion = totalExisting > 0 ? currentWeight / totalExisting : 1 / matchingIndices.length;
          const additionalWeight = data.weight * proportion;

          return {
            ...entry,
            material_weight: (currentWeight + additionalWeight).toFixed(2)
          };
        }));
      }
    }
    // hasChanges is computed automatically from pendingMaterials state
  };

  // ── Handler: handleConfirmSubdivide ──────────────────────────────────────

  // Handle confirmed subdivide - stages changes locally (saved when Save button is clicked)
  const handleConfirmSubdivide = () => {
    if (!subdivideSubSessionId || !subdivideReason) return;
    if (subdivideReason === 'other' && !subdivideReasonOther.trim()) return;

    const splitWeight = parseFloat(subdivideWeight);
    if (isNaN(splitWeight) || splitWeight <= 0) {
      toast.error(t('sessionDetails.invalidWeight') || 'Please enter a valid weight');
      return;
    }

    // Find the original entry in stationSplitEntries
    const originalEntryIndex = stationSplitEntries.findIndex(
      e => e.sub_session_number === subdivideSubSessionId
    );
    if (originalEntryIndex === -1) {
      toast.error('Could not find the sub-session to subdivide');
      return;
    }

    const originalEntry = stationSplitEntries[originalEntryIndex];
    const originalWeight = parseFloat(originalEntry.material_weight) || 0;

    // Validate: split weight cannot exceed original weight
    if (splitWeight >= originalWeight) {
      toast.error(t('sessionDetails.splitWeightTooLarge') || 'Split weight must be less than original weight');
      return;
    }

    // Create new entry for the split portion (staged, not saved to DB yet)
    const newEntry: StationSplitEntry = {
      // No sub_session_number means this is a new/pending entry
      station_id: subdivideStationId,
      station_name: subdivideStationId
        ? (stations.find(s => s.station_id === subdivideStationId)?.station_name || '')
        : '',
      material_weight: splitWeight.toString(),
      operator_id: null,
      packer_id: null,
      weight_mode: originalEntry.weight_mode || 'fixed',
      // Copy material info from original if present
      material_item_number: originalEntry.material_item_number,
      material_transfer_number: originalEntry.material_transfer_number,
      material_name: originalEntry.material_name,
      // Mark as subdivided from original
      subdivided_from: subdivideSubSessionId,
      subdivide_reason: subdivideReason,
      subdivide_reason_other: subdivideReason === 'other' ? subdivideReasonOther.trim() : undefined
    };

    // Update state: reduce original weight and add new entry
    setStationSplitEntries(prev => {
      const updated = [...prev];
      // Reduce original entry's weight
      updated[originalEntryIndex] = {
        ...updated[originalEntryIndex],
        material_weight: (originalWeight - splitWeight).toString()
      };
      // Add new entry at the end
      return [...updated, newEntry];
    });

    // Close modal and reset state
    setShowSubdivideModal(false);
    setSubdivideSubSessionId(null);
    setSubdivideWeight('');
    setSubdivideReason('');
    setSubdivideReasonOther('');
    setSubdivideStationId(null);

    toast.success(t('sessionDetails.subdivideStaged') || `Split ${splitWeight.toFixed(2)} lbs staged. Click Save to apply.`);
  };

  // ── Effect: Auto-start splitting mode ────────────────────────────────────

  // Auto-start splitting mode for multi-material sessions or sessions with existing sub-sessions
  useEffect(() => {
    // Wait for sessionData to load (includes materials array)
    // Auto-start for: multi-material sessions OR sessions with existing sub-sessions
    const shouldAutoStart = sessionData && canEdit && canSplit && !isSplitting && (
      (session.materials && session.materials.length > 1) ||
      subSessions.length > 0
    );
    if (shouldAutoStart) {
      handleStartSplitting();
    }
  }, [sessionData, canEdit, canSplit, subSessions.length]);

  // ── Effect: Re-populate form when subSessions load ───────────────────────

  // Re-populate form when subSessions load (to get correct weight_mode from database)
  // Also populate for read-only sessions and active sessions with existing sub-sessions
  useEffect(() => {
    // Update entries when: splitting, read-only, OR active session with sub-sessions
    const hasExistingSubSessions = subSessions.length > 0;
    const shouldPopulate = hasExistingSubSessions;
    if (shouldPopulate) {
      // Only include material_item_number for multi-material sessions (locks entries)
      // For single-material sessions, allow adding more stations to split the weight
      const isMultiMaterialSession = session.materials && session.materials.length > 1;

      // Match sub-sessions with materials by sub_session_number (not index — materials can have multiple rows per item)
      const entries: StationSplitEntry[] = subSessions.map((ss) => {
        const matchedMaterial = isMultiMaterialSession
          ? (session.materials?.find((m: any) => m.sub_session_number === ss.sub_session_number) || session.materials?.[0])
          : (session.materials?.[0] || undefined);
        return {
          sub_session_number: ss.sub_session_number,
          station_id: ss.station_id,
          station_name: ss.station_name || '',
          material_weight: String(Number(ss.initial_material_weight || 0).toFixed(2)),
          operator_id: ss.operator_employee_number?.toString() || ss.assigned_operator?.toString() || null,
          packer_id: ss.packer_employee_number?.toString() || ss.assigned_packer?.toString() || null,
          weight_mode: ss.weight_mode || 'fixed',
          material_item_number: matchedMaterial?.item_number || undefined,
          material_transfer_number: matchedMaterial?.transfer_number || undefined,
          material_name: matchedMaterial?.item_english_name || matchedMaterial?.item_number || undefined
        };
      });
      setStationSplitEntries(entries);
    }
  }, [subSessions, isSplitting, isReadOnly, session.status]);

  // ── Return ───────────────────────────────────────────────────────────────

  return {
    // Split state
    isSplitting,
    setIsSplitting,
    isSavingAssignments,
    setIsSavingAssignments,
    stationSplitEntries,
    setStationSplitEntries,

    // Add material modal state
    showAddMaterialModal,
    setShowAddMaterialModal,
    pendingMaterials,
    setPendingMaterials,

    // Subdivide modal state
    showSubdivideModal,
    setShowSubdivideModal,
    subdivideSubSessionId,
    setSubdivideSubSessionId,
    subdivideWeight,
    setSubdivideWeight,
    subdivideReason,
    setSubdivideReason,
    subdivideReasonOther,
    setSubdivideReasonOther,
    subdivideStationId,
    setSubdivideStationId,
    isSubdividing,
    setIsSubdividing,

    // Unsaved changes confirmation state
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,

    // Station change confirmation state
    showStationChangeModal,
    setShowStationChangeModal,
    stationChangeReason,
    setStationChangeReason,
    stationChangeOtherReason,
    setStationChangeOtherReason,
    pendingStationChange,
    setPendingStationChange,

    // Station conflict (priority queue) state
    stationConflictInfo,
    setStationConflictInfo,

    // Computed values
    canSplit,
    hasMultipleMaterials,
    totalAllocatedWeight,
    isSplitValid,
    entriesBelowMinWeight,
    hasChanges,

    // Handlers
    handleStartSplitting,
    removeStationEntry,
    updateStationEntry,
    handleSplitSession,
    handleSaveSubSessionAssignments,
    handleUnifiedSave,
    handleClose,
    handleConfirmDiscard,
    handleConfirmStationChange,
    handleConfirmQueuePriority,
    handleCancelQueuePriority,
    openSubdivideModal,
    handleAddMaterial,
    handleConfirmSubdivide,
  };
}
